import honeybadger from "@honeybadger-io/js";
import { GatewayTimeoutError } from "backend/fetcher";
import { getServerInfo } from "./server";
import { buildVersion } from "./version";

let instance: honeybadger;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
const delayedContext: any[] = [];

interface HoneybadgerFrame {
    file?: string;
    function?: string;
    method?: string;
    number?: number;
}

interface HoneybadgerNotice {
    name?: string;
    message?: string;
    stack?: string;
    backtrace?: HoneybadgerFrame[];
}

const THIRD_PARTY_DOMAINS = ["helpscout.net"];

const isEntirelyFromThirdPartyDomains = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.backtrace?.length) {
        return false;
    }

    return notice.backtrace.every(frame =>
        THIRD_PARTY_DOMAINS.some(domain => frame?.file?.includes(domain)),
    );
};

const isThirdPartyChunkLoadError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "ChunkLoadError") {
        return false;
    }
    return THIRD_PARTY_DOMAINS.some(domain => notice.message?.includes(domain));
};

const isReactPdfRaceCondition = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message?.includes("getComputedStyle")) {
        return false;
    }
    if (notice?.stack?.includes("pdf")) {
        return true;
    }
    return notice?.backtrace?.some(frame => frame?.file?.includes("pdf")) ?? false;
};

const isMaplibreInternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack?.includes("maplibre")) {
        return true;
    }
    if (notice?.backtrace?.some(frame => frame?.file?.includes("maplibre"))) {
        return true;
    }
    const maplibreMessagePatterns = ["is not iterable"];
    const isMaplibreMessage = maplibreMessagePatterns.some(pattern =>
        notice?.message?.includes(pattern),
    );
    const allFramesAreMapChunk =
        notice?.backtrace?.length > 0 &&
        notice.backtrace.every((frame: HoneybadgerFrame) =>
            frame?.file?.includes("/map-"),
        );
    return isMaplibreMessage && allFramesAreMapChunk;
};

export const isExternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack && /chrome-extension:\/\//.test(notice.stack)) {
        return true;
    }

    if (!notice?.backtrace?.length) {
        return false;
    }

    const allFramesAreUnknown = notice.backtrace.every(
        (frame: HoneybadgerFrame) =>
            frame?.file?.includes("chrome-extension://") ||
            frame?.file?.includes("<unknown>") ||
            frame?.file?.match(/<anonymous.*>/),
    );

    if (allFramesAreUnknown) {
        return true;
    }

    return isEntirelyFromThirdPartyDomains(notice);
};

export const shouldFilterDynamicImportError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    if (notice.message.includes("Failed to fetch dynamically imported module")) {
        return true;
    }

    const isPdfWorkerLoadError =
        notice.message.includes("error loading dynamically imported module") &&
        (notice.message.includes("pdf.worker") || notice.message.includes("pdfjs-dist"));

    return isPdfWorkerLoadError;
};

export const isFramerMotionScrollError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message || !notice?.stack) {
        return false;
    }

    const isScrollLeftNullError =
        notice.message.includes("Cannot read properties of null") &&
        notice.message.includes("scrollLeft");

    if (!isScrollLeftNullError) {
        return false;
    }

    return notice.stack.includes("measureScroll");
};

export const isEmptyObjectRejectionWithNoUsefulTrace = (
    notice: HoneybadgerNotice,
): boolean => {
    if (
        notice?.message !== "UnhandledPromiseRejectionWarning: {}" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: Unspecified reason" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: undefined"
    ) {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    return notice.backtrace.every((frame, _, arr) => {
        const methodName = frame?.method || frame?.function;
        const isMinifiedMethod =
            !methodName || methodName === "<unknown>" || methodName.length <= 2;
        const isSameLineAsOthers = arr.every(f => f.number === frame.number);
        return isMinifiedMethod && isSameLineAsOthers;
    });
};

export const isAbortError = (notice: HoneybadgerNotice): boolean => {
    return (
        notice?.name === "AbortError" &&
        !!notice?.message?.includes("The operation was aborted")
    );
};

export const isWebkitMaskedUrlError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.stack?.includes("webkit-masked-url://hidden/")) {
        return false;
    }

    const unspecifiedReasonMessages = [
        "UnhandledPromiseRejectionWarning: Unspecified reason",
        "UnhandledPromiseRejectionWarning: {}",
        "UnhandledPromiseRejectionWarning: undefined",
    ];

    return unspecifiedReasonMessages.some(msg => notice?.message === msg);
};

export const isMapboxGlError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    const mapboxPatterns = [
        "opacityVertexArray.length",
        "layoutVertexArray.length",
        "Could not compile fragment shader",
        "Could not compile vertex shader",
    ];

    return mapboxPatterns.some(pattern => notice.message?.includes(pattern));
};

export const isFailedToFetchWithNoUsefulTrace = (notice: HoneybadgerNotice): boolean => {
    if (notice?.message !== "Failed to fetch") {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    const isHoneybadgerInternal = notice.backtrace.some(
        frame =>
            frame?.method === "Object.postUserData" ||
            frame?.method === "postUserData" ||
            frame?.method?.includes("___hb"),
    );
    if (isHoneybadgerInternal) {
        return true;
    }

    return false;
};

const isFromReactDomOrVendorBundle = (notice: HoneybadgerNotice): boolean => {
    if (
        notice.stack?.includes("react-dom") ||
        notice.stack?.includes("react-dom.production")
    ) {
        return true;
    }

    if (notice?.backtrace?.some(frame => frame?.file?.includes("/vendor-"))) {
        return true;
    }

    return false;
};

export const isDomManipulationErrorFromExtension = (
    notice: HoneybadgerNotice,
): boolean => {
    if (!notice?.message) {
        return false;
    }

    const domManipulationPatterns = [
        "Failed to execute 'removeChild' on 'Node'",
        "Failed to execute 'insertBefore' on 'Node'",
        "Failed to execute 'appendChild' on 'Node'",
        "The node to be removed is not a child of this node",
    ];

    if (!isFromReactDomOrVendorBundle(notice)) {
        return false;
    }

    return domManipulationPatterns.some(pattern => notice.message?.includes(pattern));
};

export const isLikelyCefSharpBotError = (notice: HoneybadgerNotice): boolean => {
    return !!notice?.message?.match(/Object Not Found Matching Id:\d+, MethodName:\w+/);
};

export const isGatewayTimeoutError = (error: unknown): boolean => {
    return error instanceof GatewayTimeoutError;
};

export const shouldSkipReporting = (error: unknown): boolean => {
    if (error && typeof error === "object") {
        return !!(error as Record<string, unknown>).skipReporting;
    }

    return false;
};

const setupUnhandledRejectionHandler = () => {
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
        if (!instance) {
            return;
        }

        const reason = event.reason;

        if (shouldSkipReporting(reason)) {
            return;
        }

        if (reason instanceof Error) {
            const stack = reason.stack || `${reason.message}\n    at ? (unknown:0)`;
            const err = {
                name: reason.name,
                message: `UnhandledPromiseRejectionWarning: ${reason}`,
                stack: stack,
            };

            instance.addBreadcrumb(`window.onunhandledrejection: ${err.name}`, {
                category: "error",
                metadata: err,
            });

            instance.notify(reason);
            return;
        }

        const stringified = JSON.stringify(reason);
        const message =
            typeof reason === "string"
                ? reason
                : stringified && stringified !== "{}"
                  ? stringified
                  : "Unspecified reason";

        instance.addBreadcrumb(`window.onunhandledrejection: ${message}`, {
            category: "error",
            metadata: { reason },
        });

        instance.notify({
            name: "UnhandledPromiseRejection",
            message: `UnhandledPromiseRejectionWarning: ${message}`,
        });
    });
};

export const configureErrorTracking = async () => {
    const serverInfo = await getServerInfo();
    if (!instance) {
        honeybadger.beforeNotify((notice): boolean | void => {
            if (
                notice?.name === "ClientGraphqlNetworkError" ||
                notice?.name === "ClientHttpNetworkError" ||
                notice?.name === "UnauthenticatedError"
            ) {
                return false;
            }

            if (notice?.message?.includes("ResizeObserver")) {
                return false;
            }

            if (isFramerMotionScrollError(notice)) {
                return false;
            }

            if (shouldFilterDynamicImportError(notice)) {
                return false;
            }

            if (
                notice?.message?.includes("Unexpected keyword 'import'") &&
                (notice?.backtrace?.some(frame => frame?.file?.includes("<unknown>")) ||
                    notice?.stack?.includes("blob:"))
            ) {
                return false;
            }

            if (isMapboxGlError(notice)) {
                return false;
            }

            if (isMaplibreInternalError(notice)) {
                return false;
            }

            if (isFailedToFetchWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isLikelyCefSharpBotError(notice)) {
                return false;
            }

            // Filter MetaMask browser extension errors
            if (notice?.message?.includes("Failed to connect to MetaMask")) {
                return false;
            }

            if (isWebkitMaskedUrlError(notice)) {
                return false;
            }

            if (isExternalError(notice)) {
                return false;
            }

            if (isThirdPartyChunkLoadError(notice)) {
                return false;
            }

            if (isReactPdfRaceCondition(notice)) {
                return false;
            }

            if (isEmptyObjectRejectionWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isDomManipulationErrorFromExtension(notice)) {
                return false;
            }

            if (isAbortError(notice)) {
                return false;
            }
        });
        instance = honeybadger.configure({
            apiKey: "6502fcd2",
            environment: serverInfo.environment,
            revision: buildVersion,
            enableUnhandledRejection: false,
        });

        instance.setContext({
            serverVersion: serverInfo.build,
        });

        setupUnhandledRejectionHandler();
    }

    delayedContext.forEach(x => x(instance));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReport = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        honeybadger.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReportOnInstance = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        instance.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testFingerprint = () => {
    reportError(new Error("Test error with fingerprint"), {
        fingerprint: "test-fingerprint-123",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testParams = () => {
    reportError(new Error("Test error with params"), {
        params: { userId: "test-user-456", action: "button-click", count: 42 },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testContext = () => {
    reportError(new Error("Test error with context"), {
        context: { feature: "error-tracking-test", environment: "manual-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testAllOptions = () => {
    reportError(new Error("Test error with all options"), {
        fingerprint: "combined-test-fingerprint",
        params: { source: "manual-test", timestamp: Date.now() },
        context: { testType: "full-options-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testDuplicateFingerprint = () => {
    reportError(new Error("First error - should dedupe"), {
        fingerprint: "dedupe-test-fingerprint",
    });
    reportError(new Error("Second error - same fingerprint"), {
        fingerprint: "dedupe-test-fingerprint",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export const setErrorTrackingContext = (context: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const callback = (i: any) => i.setContext(context);
    if (instance?.setContext) {
        callback(instance);
    } else {
        delayedContext.push(callback);
    }
};

export interface ReportErrorOptions {
    fingerprint?: string;
    params?: Record<string, unknown>;
    context?: Record<string, unknown>;
}

export const reportError = (e: Error, options: ReportErrorOptions = {}) => {
    if (!instance) return;
    const notifyOptions: Record<string, unknown> = {};
    if (options.fingerprint) {
        notifyOptions.fingerprint = options.fingerprint;
    }
    if (options.params) {
        notifyOptions.params = options.params;
    }
    if (options.context) {
        notifyOptions.context = options.context;
    }
    instance.notify(e, Object.keys(notifyOptions).length > 0 ? notifyOptions : undefined);
};
